import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../store';
import { HYDRATE } from 'next-redux-wrapper';
import { WebAppMessage, WebAppType } from '@/types';
import { Token } from '@remodeling/auth';

// Define a type for the slice state

export interface WebAppState {
  isBind: boolean;
  webAppType: WebAppType | null;
  sentMessage: WebAppMessage<unknown> | null;
  receivedMessage: WebAppMessage<unknown> | null;
  isTokenRefreshed: boolean;
  tokenToSend: Token | null;
  webAppVersion: number | null;
}

// Define the initial state using that type
const initialState: WebAppState = {
  isBind: false,
  webAppType: null,
  sentMessage: null,
  receivedMessage: null,
  isTokenRefreshed: false,
  tokenToSend: null,
  webAppVersion: null,
};

export const webAppSlice = createSlice({
  name: 'webApp',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    init: (
      state,
      action: PayloadAction<{ type: WebAppType; version?: number }>,
    ) => {
      state.isBind = false;
      state.webAppType = action.payload.type;
      state.webAppVersion = action.payload?.version || null;
    },
    bind: (state) => {
      state.isBind = true;
    },
    send: (state, action) => {
      state.sentMessage = action.payload as WebAppMessage<unknown>;
    },
    receive: (state, action) => {
      state.receivedMessage = action.payload as WebAppMessage<unknown>;
    },
    refreshToken: (state, action: PayloadAction<Token>) => {
      state.isTokenRefreshed = true;
      state.tokenToSend = action.payload;
    },
    ackRefreshToken: (state) => {
      state.isTokenRefreshed = false;
      state.tokenToSend = null;
    },
  },
  extraReducers: {
    [HYDRATE]: (state, action) => {
      return {
        ...state,
        ...action.payload.webApp,
      };
    },
  },
});

export const { init, bind, send, receive, refreshToken, ackRefreshToken } =
  webAppSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectWebAppType = (state: RootState) => state.webApp.webAppType;
export const selectSentMessage = (state: RootState) => state.webApp.sentMessage;
export const selectReceivedMessage = (state: RootState) =>
  state.webApp.receivedMessage;
export const selectIsTokenRefreshed = (state: RootState) =>
  state.webApp.isTokenRefreshed;
export const selectTokenToSend = (state: RootState) => state.webApp.tokenToSend;
export const selectWebAppVersion = (state: RootState) =>
  state.webApp.webAppVersion;

export default webAppSlice;
